import { PopperPlacementType } from "@mui/base/Popper";
import {
  genericForwardRefMemo,
  genericMemo,
} from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down_filled.svg";
import ChevronUpIcon from "@redotech/redo-icons/chevron-up_filled.svg";
import classNames from "classnames";
import {
  CSSProperties,
  FocusEvent,
  ForwardedRef,
  MouseEvent,
  ReactNode,
  useState,
} from "react";
import { Flex } from "../../flex";
import { Icon } from "../../icon";
import { TextSizeValue } from "../../theme/typography";
import { DescriptionText, LabelText } from "../common/descriptors";
import {
  ListItemRender,
  RedoListItem,
  RedoListItemSelectedSource,
} from "../list/redo-list";
import {
  RedoListDropdown,
  dropdownSizeToListSize,
} from "../list/redo-list-dropdown";
import { TextRedoListItem } from "../list/standard-redo-list-items";
import { RedoSpinner } from "../loading/redo-spinner";
import * as redoDropdownInputCss from "./redo-dropdown-input.module.css";

export const redoDropdownInputSize = ["xxs", "xs", "sm", "md", "lg"] as const;
export type RedoDropdownInputSize = (typeof redoDropdownInputSize)[number];

export const redoDropdownInputState = ["default", "error", "readonly"] as const;
export type RedoDropdownInputState = (typeof redoDropdownInputState)[number];

export const RedoSingleSelectDropdownInput = genericMemo(
  function RedoSingleSelectDropdownInput<T>({
    "data-arbiter": dataArbiter = "RedoSingleSelectDropdownInput",
    "data-dd-action-name": actionName,
    ariaLabel,
    autoFocus,
    options,
    optionSelected,
    disabledItems,
    disabledTooltips,
    selectedItem,
    emptyListMessage,
    disabled = false,
    readonly = false,
    placeholder,
    className,
    size = "xs",
    fitToAnchor = true,
    label,
    description,
    onBlur,
    flexGrow = true,
    fullWidth = false,
    overflow,
    searchString,
    setSearchString,
    searchPlaceholder,
    itemsLoading,
    dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
    hideBorder,
    dataTestId,
    state = "default",
    disableKeyboardNavigation = false,
    onOptionMouseEnter,
    onOptionMouseLeave,
    uiSelector,
    dropdownPlacement,
    flip,
    virtualize,
    virtualItemSize,
    virtualMaxHeight,
    virtualOverscan,
    onEndReached,
    loadingMore,
  }: {
    "data-arbiter"?: string;
    "data-dd-action-name"?: string;
    ariaLabel?: string;
    autoFocus?: boolean;
    options: RedoListItem<T>[];
    optionSelected(args: {
      item: RedoListItem<T>;
      source: RedoListItemSelectedSource;
      index: number;
      selected: boolean;
    }): void;
    disabledItems?: (string | number)[] | (string | number);
    disabledTooltips?: Record<string | number, string>;
    selectedItem?: RedoListItem<T>;
    size?: RedoDropdownInputSize;
    emptyListMessage?: string;
    itemsLoading?: boolean;
    disabled?: boolean;
    readonly?: boolean;
    placeholder?: string;
    className?: string;
    fitToAnchor?: boolean;
    label?: string | ReactNode;
    onBlur?: () => void;
    description?: string;
    flexGrow?: boolean;
    fullWidth?: boolean;
    overflow?: "hidden" | undefined;
    searchString?: string;
    setSearchString?: (search: string) => void;
    searchPlaceholder?: string;
    dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
    hideBorder?: boolean;
    dataTestId?: string;
    state?: RedoDropdownInputState;
    disableKeyboardNavigation?: boolean;
    onOptionMouseEnter?: (item: RedoListItem<T>) => void;
    onOptionMouseLeave?: () => void;
    uiSelector?: string;
    dropdownPlacement?: PopperPlacementType;
    flip?: boolean;
    /**
     * Virtualize the dropdown list so only visible rows mount. Recommended
     * when the number of options can grow past a few hundred (e.g. parcels,
     * tags). See `RedoList` for tuning knobs.
     */
    virtualize?: boolean;
    virtualItemSize?: number;
    virtualMaxHeight?: number;
    virtualOverscan?: number;
    /** Cursor-paged options: called when the last option scrolls into view. */
    onEndReached?: () => void;
    /** Shows a trailing spinner while more options are being fetched. */
    loadingMore?: boolean;
  }) {
    const [dropdownButtonRef, setDropdownButtonRef] =
      useState<HTMLButtonElement | null>(null);
    const [dropdownOpen, setDropdownOpen] = useState(autoFocus || false);

    const handleDropdownStateChange = useHandler((open: boolean) => {
      setSearchString?.("");
      setDropdownOpen(open);
      if (!open) {
        onBlur?.();
      }
    });

    const descriptorFontSize = sizeToDescriptorTextProps[size];

    const onItemSelected = useHandler(
      (args: {
        item: RedoListItem<T>;
        source: RedoListItemSelectedSource;
        index: number;
        selected: boolean;
      }) => {
        handleDropdownStateChange(false);
        optionSelected(args);
      },
    );

    return (
      <>
        <Flex
          data-arbiter={dataArbiter}
          data-ui={uiSelector}
          dir="column"
          gap="sm"
          grow={flexGrow ? 1 : undefined}
          overflow={overflow}
          w={fullWidth ? "full" : undefined}
        >
          {typeof label === "string" ? (
            <LabelText label={label} size={descriptorFontSize} />
          ) : (
            label
          )}
          <RedoSingleSelectDropdownInputButton
            ariaLabel={ariaLabel}
            className={className}
            dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={
              dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
            }
            data-dd-action-name={actionName}
            dataTestId={dataTestId}
            disabled={disabled}
            hideBorder={hideBorder}
            isDropdownOpen={dropdownOpen}
            loading={itemsLoading}
            onBlur={(e) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              const relatedTarget = e.relatedTarget as HTMLElement | null;

              // If relatedTarget is null (clicking non-focusable element)
              // or relatedTarget is inside dropdown (in same DOM tree), defer to ClickAwayListener
              if (
                !relatedTarget ||
                relatedTarget.closest("[data-portal-selector='redo-dropdown']")
              ) {
                return;
              }

              // Covers case where relatedTarget is a shadow host that has dropdown buttons
              // this is the case for Shopify Contact Forms
              if (
                relatedTarget.shadowRoot?.querySelector(
                  "[data-portal-selector='redo-dropdown']",
                )
              ) {
                return;
              }

              handleDropdownStateChange(false);
            }}
            onClick={(e) => {
              handleDropdownStateChange(!dropdownOpen);
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape" && dropdownOpen) {
                e.preventDefault();
                handleDropdownStateChange(false);
              }
            }}
            placeholder={placeholder}
            readonly={readonly}
            ref={setDropdownButtonRef}
            selectedItem={selectedItem}
            size={size}
            state={state}
          />
          <DescriptionText
            description={description}
            error={state === "error"}
            size={descriptorFontSize}
          />
        </Flex>
        <RedoListDropdown
          dangerousStyleThatShouldOnlyBeUsedForMerchantBranding={
            dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
          }
          disabledItems={disabledItems}
          disabledTooltips={disabledTooltips}
          disableKeyboardNavigation={disableKeyboardNavigation}
          dropdownAnchor={dropdownButtonRef}
          dropdownOpen={dropdownOpen}
          dropdownPlacement={dropdownPlacement}
          emptyListMessage={emptyListMessage}
          fitToAnchor={fitToAnchor}
          flip={flip}
          items={options}
          itemSelected={onItemSelected}
          itemsLoading={itemsLoading}
          key={`${dropdownOpen}`}
          loadingMore={loadingMore}
          onEndReached={onEndReached}
          onItemMouseEnter={onOptionMouseEnter}
          onItemMouseLeave={onOptionMouseLeave}
          searchPlaceholder={searchPlaceholder}
          searchString={searchString}
          selectedItems={selectedItem?.id}
          setDropdownOpen={handleDropdownStateChange}
          setSearchString={setSearchString}
          size={size}
          virtualItemSize={virtualItemSize}
          virtualize={virtualize}
          virtualMaxHeight={virtualMaxHeight}
          virtualOverscan={virtualOverscan}
        />
      </>
    );
  },
);

interface RedoSingleSelectDropdownInputButtonProps<T> {
  "data-dd-action-name"?: string;
  ariaLabel?: string;
  size?: RedoDropdownInputSize;
  isDropdownOpen: boolean;
  selectedItem?: RedoListItem<T>;
  disabled?: boolean;
  readonly?: boolean;
  placeholder?: string;
  className?: string;
  onBlur?: (e: FocusEvent<HTMLButtonElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
  onClick?: (e: MouseEvent) => void;
  ref?: any;
  hideBorder?: boolean;
  dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?: React.CSSProperties;
  dataTestId?: string;
  loading?: boolean;
  state?: RedoDropdownInputState;
}

export const RedoSingleSelectDropdownInputButton = genericForwardRefMemo(
  function RedoSingleSelectDropdownInputButton<T>(
    {
      ariaLabel,
      selectedItem,
      isDropdownOpen,
      size = "sm",
      disabled = false,
      readonly = false,
      placeholder,
      className,
      loading = false,
      hideBorder = false,
      onBlur,
      onKeyDown,
      onClick,
      dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
      dataTestId,
      "data-dd-action-name": actionName,
      state = "default",
    }: RedoSingleSelectDropdownInputButtonProps<T>,
    ref: ForwardedRef<HTMLButtonElement>,
  ) {
    const buttonOverrideStyles: CSSProperties = {
      ...dangerousStyleThatShouldOnlyBeUsedForMerchantBranding,
      ...(dangerousStyleThatShouldOnlyBeUsedForMerchantBranding?.outlineColor
        ? {
            borderColor:
              dangerousStyleThatShouldOnlyBeUsedForMerchantBranding.outlineColor,
            borderWidth: "1px",
            borderStyle: "solid",
          }
        : {}),
      ...(hideBorder && { borderWidth: 0, paddingTop: 0, paddingBottom: 0 }),
    };

    const content = (
      <Flex
        align="center"
        data-arbiter="RedoSingleSelectDropdownInputButton"
        dir="row"
        gap="sm"
        grow={1}
        overflow="hidden"
      >
        {selectedItem ? (
          selectedItem.type === "custom" ? (
            <Flex grow={1} overflow="hidden">
              {selectedItem.render}
            </Flex>
          ) : (
            <ListItemRender
              card={false}
              customStyles={
                dangerousStyleThatShouldOnlyBeUsedForMerchantBranding
              }
              disabled={disabled}
              focused={false}
              idx={0}
              item={selectedItem}
              selected={false}
              selectionVariant="none"
              size={dropdownSizeToListSize[size]}
              useWrapper={false}
            />
          )
        ) : (
          <TextRedoListItem
            customStyles={dangerousStyleThatShouldOnlyBeUsedForMerchantBranding}
            placeholder
            size={dropdownSizeToListSize[size]}
            text={placeholder || ""}
            useWrapper={false}
          />
        )}
      </Flex>
    );

    if (readonly) {
      return content;
    }

    return (
      <button
        aria-label={ariaLabel}
        className={classNames(
          hideBorder && redoDropdownInputCss.hideBorder,
          redoDropdownInputCss.singleSelectInput,
          sizeClassToCssClass[size],
          isDropdownOpen && redoDropdownInputCss.open,
          disabled && redoDropdownInputCss.disabled,
          state === "error" && redoDropdownInputCss.error,
          className,
        )}
        data-arbiter="RedoSingleSelectDropdownInputButton"
        data-dd-action-name={actionName}
        data-portal-selector="redo-select-input"
        data-testid={dataTestId}
        disabled={disabled || readonly}
        onBlur={onBlur}
        onClick={onClick}
        onKeyDown={onKeyDown}
        ref={ref}
        style={buttonOverrideStyles}
        type="button"
      >
        {content}
        {loading ? (
          <RedoSpinner size="xs" />
        ) : (
          <Icon
            arbiterIconSvg={isDropdownOpen ? ChevronUpIcon : ChevronDownIcon}
            className={redoDropdownInputCss.chevron}
            color="ghost"
          />
        )}
      </button>
    );
  },
);

const sizeToDescriptorTextProps: Record<RedoDropdownInputSize, TextSizeValue> =
  { xxs: "xxs", xs: "xs", sm: "xs", md: "sm", lg: "sm" };

const sizeClassToCssClass: Record<RedoDropdownInputSize, string | undefined> = {
  xxs: redoDropdownInputCss.xxSmall,
  xs: redoDropdownInputCss.xSmall,
  sm: redoDropdownInputCss.small,
  md: redoDropdownInputCss.regular,
  lg: redoDropdownInputCss.large,
};
